import { RefreshCw, X } from 'lucide-react'
import { useCallback, useId, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { type LogEntry, runSheetsCheck, toLogEntry } from './sheets-status'

/**
 * Hidden Google Sheets diagnostic panel.
 *
 * Revealed by tapping the employee ID five times (see `useSecretTap`), so it
 * stays invisible to trainees while remaining reachable on a real device where
 * there is no console and no devtools.
 *
 * Every run is appended to a log rather than replacing the last result: the
 * question this answers is usually "did the grant I just made take effect?",
 * which needs before and after side by side.
 */

const LEVEL_STYLES: Record<LogEntry['level'], string> = {
  ok: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  warn: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  fail: 'border-rose-500/40 bg-rose-500/10 text-rose-300',
}

export function SheetsDiagnosticsPanel({ onClose }: Readonly<{ onClose: () => void }>) {
  const [log, setLog] = useState<LogEntry[]>([])
  const [busy, setBusy] = useState(false)
  const seq = useRef(0)
  const titleId = useId()

  const run = useCallback(async (write: boolean) => {
    setBusy(true)
    try {
      const res = await runSheetsCheck({ write })
      seq.current += 1
      // Newest first — on a phone the latest run must not be below the fold.
      setLog((prev) => [toLogEntry(res, `run-${seq.current}`), ...prev])
    } finally {
      setBusy(false)
    }
  }, [])

  return (
    // A native <dialog> rather than role="dialog": it carries the modal
    // semantics for free. `open` is set declaratively because the panel's
    // visibility is owned by the caller, not by showModal().
    <dialog
      open
      className="fixed inset-0 z-50 flex h-full max-h-none w-full max-w-none flex-col bg-slate-950/95 text-slate-100 backdrop-blur-sm"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div className="flex flex-col">
          <span id={titleId} className="text-sm font-bold tracking-tight">
            Sheets diagnostics
          </span>
          <span className="text-xs text-slate-400">Hidden panel — not part of the training UI</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close diagnostics"
          className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition-all active:scale-[0.98]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex gap-2 border-b border-white/10 px-4 py-3">
        <button
          type="button"
          onClick={() => void run(false)}
          disabled={busy}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-50"
        >
          <RefreshCw className={cn('h-4 w-4', busy && 'animate-spin')} />
          Check connection
        </button>
        <button
          type="button"
          onClick={() => void run(true)}
          disabled={busy}
          // Separate control, never the default: this one modifies the sheet.
          className="rounded-xl border border-amber-400/40 bg-amber-500/15 px-3 py-2.5 text-sm font-semibold text-amber-200 transition-all active:scale-[0.98] disabled:opacity-50"
        >
          Check + write
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {log.length === 0 ? (
          <p className="text-sm text-slate-400">
            No runs yet. “Check connection” only reads. “Check + write” stamps cell A1 of the first
            tab and reads it back.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {log.map((entry) => (
              <li
                key={entry.id}
                className={cn('rounded-xl border px-3 py-2', LEVEL_STYLES[entry.level])}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-bold">{entry.title}</span>
                  <span className="shrink-0 text-xs opacity-70">{entry.at}</span>
                </div>
                <pre className="mt-1 overflow-x-auto font-mono text-[11px] leading-relaxed whitespace-pre-wrap text-slate-300">
                  {entry.lines.join('\n')}
                </pre>
              </li>
            ))}
          </ul>
        )}
      </div>
    </dialog>
  )
}
